'use client'

import { ReactNode, useCallback, useEffect, useState } from 'react'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useSession } from '@marketplace-web/shared/session-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { logBrazeMessage } from '@marketplace-web/braze/braze-data'

import { InitializeBrazeReturnType } from '../../unsafe-initialize'
import { requestContentCardsRefreshThrottled } from '../../utils/async-utils'
import { flushCustomEventsThrottled } from '../../utils/custom-event'
import { getCardLoggersById } from '../../utils/event-loggers'
import BrazeContext, { initialBrazeContextValue } from './BrazeContext'
import useInitializeBraze from './useInitializeBraze'
import useManageInAppMessage from './useManageInAppMessage'

const AUTO_TEST_USER_EMAIL_ROOT = 'autotestvinted'

type Props = {
  children: ReactNode
}

const BrazeProvider = ({ children }: Props) => {
  const [userExternalId, setUserExternalId] = useState<string | null>(null)
  const [providers, setProviders] = useState<Awaited<InitializeBrazeReturnType>['providers']>()
  const [contextValue, setContextValue] = useState({
    ...initialBrazeContextValue,
    setUserExternalId,
  })
  const { user } = useSession()
  const [isInitialized, setIsInitialized] = useState(false)
  const { relativeUrl } = useBrowserNavigation()
  const isAutoTestUser =
    (user?.email?.includes(AUTO_TEST_USER_EMAIL_ROOT) && user?.email?.endsWith('@vinted.com')) ||
    false

  const isFetchExternalIdEnabled = useFeatureSwitch('kk_migrate_external_id_retrieval_web')
  const userDtoExternalId = user?.external_id || null
  const externalId = isFetchExternalIdEnabled ? userExternalId : userDtoExternalId

  const initializeBraze = useInitializeBraze(externalId)
  useManageInAppMessage(providers?.inAppMessageProvider)

  useEffect(() => {
    if (!externalId && !isAutoTestUser) {
      // Temporary logging for monitoring new logic
      logBrazeMessage(
        `ExternalID FS: ${isFetchExternalIdEnabled ? 'ON' : 'OFF'} Attempted Braze init without externalId`,
      )

      return
    }

    initializeBraze().then(({ initialized, stores, providers: newProviders }) => {
      const cardStores = [
        stores.inboxMessageCardStore,
        stores.inboxNotificationCardStore,
        stores.promoBoxCardStore,
      ]
      const cardLoggers = getCardLoggersById(cardStores)

      if (newProviders) setProviders(newProviders)

      setIsInitialized(initialized)
      setContextValue({
        ...stores,
        ...cardLoggers,
        userExternalId: externalId,
        setUserExternalId,
      })
    })
  }, [initializeBraze, externalId, isFetchExternalIdEnabled, isAutoTestUser])

  const handleRelativeUrlChange = useCallback(() => {
    if (!externalId) return
    if (!isInitialized) return

    flushCustomEventsThrottled(externalId)
    requestContentCardsRefreshThrottled()
  }, [isInitialized, externalId])

  useEffect(() => {
    handleRelativeUrlChange()
  }, [relativeUrl, handleRelativeUrlChange])

  return <BrazeContext.Provider value={contextValue}>{children}</BrazeContext.Provider>
}

export default BrazeProvider
